import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { api, newIdempotencyKey } from "../lib/api";
import { customerLabel, money, parseMoney } from "../lib/format";
import { PageHeader } from "../components/Layout";
import {
  Button,
  Card,
  DataState,
  ErrorBanner,
  Field,
  Input,
  Notice,
  Select,
  StatusBadge,
  Table,
} from "../components/ui";
import { QrPayment } from "../components/QrPayment";
import type { Invoice, PaymentAttempt } from "../lib/types";

type ProviderInfo = {
  selected: string;
  selectedIsImplemented: boolean;
  available: Array<{
    name: string;
    capabilities: {
      onlineCheckout: boolean;
      terminal: boolean;
      partialRefunds: boolean;
      methods: string[];
    };
  }>;
};

export function PosPage() {
  const { invoiceId } = useParams<{ invoiceId?: string }>();
  const navigate = useNavigate();

  const unpaid = useQuery({
    queryKey: ["invoices", "unpaid"],
    queryFn: () => api.get<{ data: Invoice[] }>("/invoices?unpaid=true"),
  });

  if (invoiceId) return <CollectPayment invoiceId={invoiceId} />;

  return (
    <>
      <PageHeader
        title="Point of sale"
        description="Pick an invoice to collect against. A payment is only complete when the processor confirms it."
      />

      <Card title="Open balances">
        <DataState
          loading={unpaid.isLoading}
          error={unpaid.error}
          empty={unpaid.data?.data.length === 0}
          emptyMessage="Nothing outstanding. Every issued invoice is settled."
        >
          <Table headers={["Invoice", "Customer", "Job", "Total", "Balance", "Status", ""]}>
            {unpaid.data?.data.map((inv) => (
              <tr key={inv.id} className="hover:bg-slate-50">
                <td className="px-3 py-2 font-mono text-xs">{inv.number}</td>
                <td className="px-3 py-2">{customerLabel(inv.customer)}</td>
                <td className="px-3 py-2 font-mono text-xs text-slate-500">
                  {inv.repairOrder?.number ?? "—"}
                </td>
                <td className="px-3 py-2 tabular-nums text-slate-600">{money(inv.total)}</td>
                <td className="px-3 py-2 font-medium tabular-nums">{money(inv.balance)}</td>
                <td className="px-3 py-2">
                  <StatusBadge status={inv.status} />
                </td>
                <td className="px-3 py-2 text-right">
                  <Button variant="secondary" onClick={() => navigate(`/pos/${inv.id}`)}>
                    Collect
                  </Button>
                </td>
              </tr>
            ))}
          </Table>
        </DataState>
      </Card>
    </>
  );
}

function CollectPayment({ invoiceId }: { invoiceId: string }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const invoice = useQuery({
    queryKey: ["invoice", invoiceId],
    queryFn: () => api.get<Invoice>(`/invoices/${invoiceId}`),
  });

  const providers = useQuery({
    queryKey: ["payment-providers"],
    queryFn: () => api.get<ProviderInfo>("/payments/providers"),
  });

  const balance = invoice.data
    ? invoice.data.total - invoice.data.amountPaid - invoice.data.amountCredited
    : 0;

  const [method, setMethod] = useState("CARD_TERMINAL");
  const [amountInput, setAmountInput] = useState("");
  const [attempt, setAttempt] = useState<PaymentAttempt | null>(null);

  /**
   * One key per intended payment, generated when the form opens rather than on
   * each click. A double submit, a refresh or a network retry all resolve to
   * the same attempt (build guide / 09).
   */
  const idempotencyKey = useMemo(() => newIdempotencyKey(), [invoiceId]);

  const amount = parseMoney(amountInput || String(balance / 100));
  const isManual = method === "CASH" || method === "CHECK";
  const isQr = method === "QR_CODE";

  const startCard = useMutation({
    mutationFn: () =>
      api.post<PaymentAttempt>("/payments/attempts", {
        invoiceId,
        amount,
        method,
        idempotencyKey,
      }),
    onSuccess: setAttempt,
  });

  const takeManual = useMutation({
    mutationFn: () =>
      api.post("/payments/manual", { invoiceId, amount, method, idempotencyKey }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["invoice", invoiceId] });
      void queryClient.invalidateQueries({ queryKey: ["invoices"] });
      setAmountInput("");
    },
  });

  // Demo-only: nudge the mock processor, then resolve authoritatively.
  const simulate = useMutation({
    mutationFn: (outcome: "settle" | "fail") =>
      api.post(`/payments/attempts/${attempt!.id}/simulate`, { outcome }),
  });

  const resolve = useMutation({
    mutationFn: () => api.post<PaymentAttempt>(`/payments/attempts/${attempt!.id}/resolve`),
    onSuccess: (result) => {
      setAttempt(result);
      void queryClient.invalidateQueries({ queryKey: ["invoice", invoiceId] });
      void queryClient.invalidateQueries({ queryKey: ["invoices"] });
    },
  });

  const inv = invoice.data;
  const providerBroken = providers.data && !providers.data.selectedIsImplemented;
  const selectedCapabilities = providers.data?.available.find(
    (p) => p.name === providers.data!.selected,
  )?.capabilities;
  const qrSupported = !providerBroken && (selectedCapabilities?.methods.includes("QR_CODE") ?? false);

  return (
    <>
      <PageHeader
        title={inv ? `Collect on ${inv.number}` : "Collect payment"}
        description={inv ? customerLabel(inv.customer) : undefined}
        action={
          <Button variant="secondary" onClick={() => navigate("/pos")}>
            Back to open balances
          </Button>
        }
      />

      <DataState loading={invoice.isLoading} error={invoice.error}>
        {inv && (
          <div className="grid gap-5 lg:grid-cols-[1fr_360px]">
            <Card title="Invoice">
              <Table headers={["Line", "Qty", "Unit", "Total"]}>
                {inv.lines?.map((l) => (
                  <tr key={l.id}>
                    <td className="px-3 py-2 text-slate-600">{l.description}</td>
                    <td className="px-3 py-2 tabular-nums">{Number(l.quantity)}</td>
                    <td className="px-3 py-2 tabular-nums">{money(l.unitPrice)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{money(l.lineTotal)}</td>
                  </tr>
                ))}
              </Table>

              <dl className="mt-4 space-y-1 border-t border-slate-200 pt-3 text-sm">
                <div className="flex justify-between text-slate-600">
                  <dt>Subtotal</dt>
                  <dd className="tabular-nums">{money(inv.subtotal)}</dd>
                </div>
                <div className="flex justify-between text-slate-600">
                  <dt>Sales tax</dt>
                  <dd className="tabular-nums">{money(inv.taxTotal)}</dd>
                </div>
                <div className="flex justify-between font-semibold">
                  <dt>Total</dt>
                  <dd className="tabular-nums">{money(inv.total)}</dd>
                </div>
                <div className="flex justify-between text-slate-600">
                  <dt>Already paid</dt>
                  <dd className="tabular-nums">{money(inv.amountPaid)}</dd>
                </div>
                <div className="flex justify-between text-base font-semibold text-ink">
                  <dt>Balance due</dt>
                  <dd className="tabular-nums">{money(balance)}</dd>
                </div>
              </dl>

              {(inv.attempts?.length ?? 0) > 0 && (
                <div className="mt-5">
                  <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Payment attempts
                  </h3>
                  <Table headers={["Provider", "Method", "Amount", "Status"]}>
                    {inv.attempts?.map((a) => (
                      <tr key={a.id}>
                        <td className="px-3 py-2 text-slate-600">{a.provider}</td>
                        <td className="px-3 py-2 text-slate-600">
                          {a.method.toLowerCase().replace("_", " ")}
                        </td>
                        <td className="px-3 py-2 tabular-nums">{money(a.amount)}</td>
                        <td className="px-3 py-2">
                          <StatusBadge status={a.status} />
                        </td>
                      </tr>
                    ))}
                  </Table>
                </div>
              )}
            </Card>

            <div className="space-y-4">
              {providerBroken && (
                <Notice tone="danger">
                  This shop is configured for <strong>{providers.data?.selected}</strong>, which has
                  no working adapter in this build. Card payments are unavailable; cash and check
                  still work.
                </Notice>
              )}

              {balance <= 0 ? (
                <Notice>This invoice is settled. Nothing further to collect.</Notice>
              ) : (
                <Card title="Take payment">
                  <ErrorBanner error={startCard.error} />
                  <ErrorBanner error={takeManual.error} />

                  <div className="space-y-3">
                    <Field label="Method">
                      <Select value={method} onChange={(e) => setMethod(e.target.value)}>
                        <option value="CARD_TERMINAL" disabled={providerBroken}>
                          Card — terminal
                        </option>
                        <option value="CARD_ONLINE" disabled={providerBroken}>
                          Card — send a link
                        </option>
                        <option value="QR_CODE" disabled={!qrSupported}>
                          QR code — customer scans to pay
                        </option>
                        <option value="CASH">Cash</option>
                        <option value="CHECK">Check</option>
                      </Select>
                      {method === "QR_CODE" && !qrSupported && (
                        <p className="mt-1 text-xs text-amber-700">
                          {providers.data?.selected} does not support QR checkout in this
                          deployment.
                        </p>
                      )}
                    </Field>

                    <Field
                      label="Amount"
                      hint={`Balance due is ${money(balance)}. A partial payment is allowed.`}
                    >
                      <Input
                        value={amountInput}
                        onChange={(e) => setAmountInput(e.target.value)}
                        placeholder={(balance / 100).toFixed(2)}
                        inputMode="decimal"
                        className="tabular-nums text-lg"
                      />
                    </Field>

                    <Button
                      className="w-full"
                      loading={startCard.isPending || takeManual.isPending}
                      disabled={
                        !amount || amount <= 0 || amount > balance || (isQr && !qrSupported)
                      }
                      onClick={() => (isManual ? takeManual.mutate() : startCard.mutate())}
                    >
                      {isManual
                        ? `Record ${money(amount ?? 0)}`
                        : isQr
                          ? `Generate QR for ${money(amount ?? 0)}`
                          : `Charge ${money(amount ?? 0)}`}
                    </Button>

                    {amount !== null && amount > balance && (
                      <p className="text-xs text-red-600">
                        That is more than the outstanding balance.
                      </p>
                    )}
                  </div>
                </Card>
              )}

              {attempt && (
                <Card title="Processor">
                  <div className="space-y-3 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="text-slate-600">Attempt status</span>
                      <StatusBadge status={attempt.status} />
                    </div>

                    {attempt.status === "UNKNOWN" && (
                      <Notice tone="danger">
                        <p className="font-medium">The outcome is unknown</p>
                        <p className="mt-1">
                          The processor did not answer. Do not retry on another processor. Resolve
                          this attempt first; the money may already have moved.
                        </p>
                      </Notice>
                    )}

                    {attempt.checkoutUrl && attempt.status === "PENDING" && attempt.method === "QR_CODE" && (
                      <QrPayment url={attempt.checkoutUrl} amount={attempt.amount} currency={inv.currency} />
                    )}

                    {attempt.checkoutUrl && attempt.status === "PENDING" && attempt.method !== "QR_CODE" && (
                      <div className="rounded border border-slate-200 p-2">
                        <p className="text-xs text-slate-500">Customer payment link</p>
                        <p className="break-all font-mono text-xs">{attempt.checkoutUrl}</p>
                      </div>
                    )}

                    <Notice>
                      Returning from a payment page does not mean the money moved. The status below
                      comes from asking the processor directly.
                    </Notice>

                    <div className="flex flex-wrap gap-2">
                      <Button
                        variant="secondary"
                        loading={resolve.isPending}
                        onClick={() => resolve.mutate()}
                      >
                        Check with processor
                      </Button>
                      {/* Demo controls for the mock provider only. */}
                      {attempt.provider === "mock" && attempt.status === "PENDING" && (
                        <>
                          <Button
                            variant="secondary"
                            loading={simulate.isPending}
                            onClick={() =>
                              simulate.mutate("settle", {
                                onSuccess: () => resolve.mutate(),
                              })
                            }
                          >
                            Simulate approval
                          </Button>
                          <Button
                            variant="secondary"
                            onClick={() =>
                              simulate.mutate("fail", { onSuccess: () => resolve.mutate() })
                            }
                          >
                            Simulate decline
                          </Button>
                        </>
                      )}
                    </div>

                    {attempt.failureMessage && (
                      <p className="text-xs text-red-600">{attempt.failureMessage}</p>
                    )}
                    <ErrorBanner error={resolve.error} />
                  </div>
                </Card>
              )}
            </div>
          </div>
        )}
      </DataState>
    </>
  );
}
